import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Send, Mail, ArrowUpRight,
  CalendarCheck, Shield, Clock, CheckCircle2, Loader2, User
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { fetchUserCompany } from "@/services/diagnosticService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 }
};

export default function ContactPage() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    activity: "",
    email: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userCompany, setUserCompany] = useState(null);

  // Load user company data if logged in
  useEffect(() => {
    if (user) {
      fetchUserCompany(user.id)
        .then(company => setUserCompany(company))
        .catch(() => setUserCompany(null));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Build submission data
    let submissionData;
    
    if (user) {
      // Logged in user - use profile data
      submissionData = {
        name: user.user_metadata?.full_name || user.email?.split('@')[0] || "Utilisateur",
        email: user.email,
        company: userCompany?.company_name || "",
        activity: userCompany?.industry || "",
        message: formData.message,
      };
    } else {
      // Guest user - use form data
      submissionData = formData;
    }

    if (!submissionData.message) {
      toast.error("Veuillez écrire un message.");
      return;
    }

    if (!user && (!submissionData.name || !submissionData.email)) {
      toast.error("Veuillez remplir les champs obligatoires.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, submissionData);
      toast.success(response.data.message || "Message envoyé avec succès !");
      setSubmitted(true);
      setFormData({ name: "", company: "", activity: "", email: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-16 bg-[#0B1F3B] relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#59C3A6]/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div {...fadeUp}>
            <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">Contact</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 mb-6 text-white">
              Parlons de{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#59C3A6] to-[#4AB095]">
                votre projet
              </span>
            </h1>
            <p className="text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
              Nous analysons gratuitement votre organisation et vos outils pour identifier les leviers de croissance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#0B1F3B]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <motion.div {...fadeUp} className="lg:col-span-3">
              <div className="p-8 md:p-10 rounded-2xl bg-[#112A4F]/30 border border-white/5">
                {submitted ? (
                  <div data-testid="contact-success" className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-[#59C3A6]/20 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle2 size={32} className="text-[#59C3A6]" />
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-white mb-3">
                      Message envoyé !
                    </h3>
                    <p className="text-slate-300 text-base mb-6 max-w-md mx-auto">
                      Merci pour votre intérêt. Nous vous recontacterons dans les plus brefs délais pour organiser votre diagnostic gratuit.
                    </p>
                    <Button
                      data-testid="contact-send-another"
                      onClick={() => setSubmitted(false)}
                      className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-6 py-2.5 rounded-full"
                    >
                      Envoyer un autre message
                    </Button>
                  </div>
                ) : user ? (
                  /* LOGGED IN USER - Simplified form */
                  <form onSubmit={handleSubmit} data-testid="contact-form-logged" className="space-y-5">
                    <div className="mb-6">
                      <h2 className="font-heading text-2xl font-semibold text-white mb-1">
                        Discutons de vos résultats
                      </h2>
                      <p className="text-slate-400 text-sm">
                        Posez vos questions, nous vous répondrons rapidement.
                      </p>
                    </div>

                    {/* Logged in indicator */}
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-[#59C3A6]/10 border border-[#59C3A6]/20">
                      <div className="w-10 h-10 rounded-full bg-[#59C3A6]/20 flex items-center justify-center">
                        <User size={18} className="text-[#59C3A6]" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">
                          Connecté en tant que <span className="text-[#59C3A6]">{user.email}</span>
                        </p>
                        {userCompany?.company_name && (
                          <p className="text-slate-400 text-xs">{userCompany.company_name}</p>
                        )}
                      </div>
                    </div>

                    {/* Message only */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-300 text-sm">Votre message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        data-testid="contact-message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Décrivez vos questions ou ce que vous aimeriez approfondir suite à votre diagnostic..."
                        required
                        rows={6}
                        className="bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={loading}
                      className="w-full bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold py-3 h-auto rounded-full transition-all duration-300 hover:scale-[1.02] shadow-[0_0_20px_rgba(89,195,166,0.25)] text-base"
                    >
                      {loading ? (
                        <><Loader2 className="animate-spin mr-2" size={18} /> Envoi en cours...</>
                      ) : (
                        <><Send className="mr-2" size={18} /> Envoyer mon message</>
                      )}
                    </Button>
                  </form>
                ) : (
                  /* GUEST USER - Full form */
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                    <div className="mb-6">
                      <h2 className="font-heading text-2xl font-semibold text-white mb-1">
                        Demander mon diagnostic gratuit
                      </h2>
                      <p className="text-slate-400 text-sm">Remplissez le formulaire et nous vous recontacterons rapidement.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-300 text-sm">Nom *</Label>
                        <Input
                          id="name"
                          name="name"
                          data-testid="contact-name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Votre nom"
                          required
                          className="bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-slate-300 text-sm">Entreprise</Label>
                        <Input
                          id="company"
                          name="company"
                          data-testid="contact-company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Nom de votre entreprise"
                          className="bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] h-11"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="activity" className="text-slate-300 text-sm">Activité</Label>
                        <Input
                          id="activity"
                          name="activity"
                          data-testid="contact-activity"
                          value={formData.activity}
                          onChange={handleChange}
                          placeholder="Ex: Électricien, plombier..."
                          className="bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300 text-sm">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          data-testid="contact-email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="votre@email.com"
                          required
                          className="bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] h-11"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-300 text-sm">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        data-testid="contact-message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Décrivez brièvement votre situation et vos besoins..."
                        required
                        rows={5}
                        className="bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={loading}
                      className="w-full bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold py-3 h-auto rounded-full transition-all duration-300 hover:scale-[1.02] shadow-[0_0_20px_rgba(89,195,166,0.25)] text-base"
                    >
                      {loading ? (
                        <><Loader2 className="animate-spin mr-2" size={18} /> Envoi en cours...</>
                      ) : (
                        <><Send className="mr-2" size={18} /> Demander mon diagnostic gratuit</>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* Calendly */}
              <div className="p-6 rounded-2xl bg-[#112A4F]/30 border border-[#59C3A6]/15">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center">
                    <CalendarCheck size={20} className="text-[#59C3A6]" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white">Réserver un appel</h3>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  Préférez un échange direct ? Réservez un créneau de 30 minutes pour discuter de votre projet.
                </p>
                <a
                  href="https://calendly.com/finoveo-conseil/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-calendly"
                  className="inline-flex items-center gap-2 bg-[#59C3A6]/10 text-[#59C3A6] font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-[#59C3A6]/20 transition-all border border-[#59C3A6]/20"
                >
                  Réserver mon créneau <ArrowUpRight size={14} />
                </a>
              </div>

              {/* Contact info */}
              <div className="p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5">
                <h3 className="font-heading text-lg font-semibold text-white mb-4">Nous contacter</h3>
                <div className="space-y-3">
                  <a href="mailto:finoveo.conseil@gmail.com" className="flex items-center gap-3 text-slate-400 hover:text-[#59C3A6] transition-colors text-sm">
                    <Mail size={16} className="text-[#59C3A6]" />
                    finoveo.conseil@gmail.com
                  </a>
                </div>
              </div>

              {/* Reassurance */}
              <div className="p-6 rounded-2xl bg-[#112A4F]/30 border border-white/5">
                <h3 className="font-heading text-base font-semibold text-white mb-4">Pourquoi nous contacter ?</h3>
                <div className="space-y-3">
                  {[
                    { icon: Shield, text: "Diagnostic gratuit et sans engagement" },
                    { icon: Clock, text: "Réponse sous 24h" },
                    { icon: CheckCircle2, text: "Solutions adaptées à votre budget" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <item.icon size={16} className="text-[#59C3A6] shrink-0" />
                      <span className="text-slate-400 text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
